import React, { useCallback, useEffect, useMemo, useState } from "react";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seoConfig";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Section, Kicker } from "@/components/Section";
import { EditorialImage } from "@/components/EditorialImage";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import DOMPurify from "dompurify";
import { PublicAPI } from "@/lib/api";

const fmt = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); } catch { return ""; }
};

// Allow a small editorial subset of HTML in journal posts. Inline event
// handlers (`onclick=`), `<script>`, `<iframe>`, etc. are all stripped.
const SANITIZE_CONFIG = {
    ALLOWED_TAGS: [
        "p", "br", "strong", "em", "b", "i", "u", "small",
        "blockquote", "q", "cite",
        "ul", "ol", "li",
        "h2", "h3", "h4",
        "a",
        "img",
        "figure", "figcaption",
        "hr",
        "span",
    ],
    ALLOWED_ATTR: [
        "href", "title", "alt", "src", "srcset", "sizes", "loading",
        "rel", "target", "class", "id",
    ],
    // Force every link to open safely.
    ADD_ATTR: ["rel", "target"],
};

const sanitize = (raw) =>
    DOMPurify.sanitize(raw || "", SANITIZE_CONFIG);

export default function JournalPost() {
    const { slug } = useParams();
    const nav = useNavigate();
    const [post, setPost] = useState(null);
    const [error, setError] = useState(false);

    const loadPost = useCallback((s) => {
        PublicAPI.getJournal(s).then(setPost).catch(() => setError(true));
    }, []);

    useEffect(() => {
        loadPost(slug);
    }, [slug, loadPost]);

    // Memoize the sanitized HTML so we don't re-run DOMPurify on every
    // render. Recomputes only when the post body actually changes.
    const safeBody = useMemo(
        () => (post?.body ? sanitize(post.body) : ""),
        [post?.body]
    );

    if (error) {
        return (
            <div className="pt-32 min-h-[60vh] text-center">
                <Kicker>Not found</Kicker>
                <p className="mt-3 font-serif-display text-2xl">This essay has moved or is no longer available.</p>
                <button onClick={() => nav("/journal")} className="mt-6 text-xs tracking-[0.18em] uppercase lux-underline">Back to Journal</button>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="pt-32 max-w-[760px] mx-auto px-4">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-10 w-full mt-4" />
                <Skeleton className="aspect-[16/9] w-full mt-8" />
                <Skeleton className="h-6 w-full mt-6" />
                <Skeleton className="h-6 w-3/4 mt-3" />
            </div>
        );
    }

    return (
        <div className="pt-24 sm:pt-28" data-testid="journal-article">
            <Seo
                {...SEO.journalArticle({
                    title: post.title,
                    slug: post.slug,
                    description: post.excerpt,
                })}
                ogImage={post.cover_image}
            />
            <Section spacing="tight">
                <div className="max-w-[760px] mx-auto">
                    <Link to="/journal" className="kicker lux-underline">← Journal</Link>
                    <div className="kicker mt-6">{post.category || "Essay"} · {fmt(post.created_at)}</div>
                    <h1 className="font-serif-display text-4xl sm:text-5xl mt-3 leading-[1.05]">{post.title}</h1>
                    <p className="mt-5 text-base text-muted-foreground leading-[1.85]">{post.excerpt}</p>
                </div>
            </Section>
            {post.cover_image && (
                <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
                    <EditorialImage src={post.cover_image} alt={post.title} ratio={16 / 9} />
                </div>
            )}
            <Section spacing="normal">
                <div className="max-w-[760px] mx-auto">
                    <Separator className="my-8 bg-[hsl(var(--lux-brass))]/40 w-10" />
                    <article
                        className="font-serif-display text-[19px] leading-[1.85] text-foreground/90 space-y-6 [&>p]:font-serif-display"
                        // Sanitized via DOMPurify above — strips scripts, event
                        // handlers, iframes, and any tag not on the editorial
                        // allow-list. Safe to render.
                        dangerouslySetInnerHTML={{ __html: safeBody }}
                    />
                </div>
            </Section>
        </div>
    );
}
